import React from 'react';
import { useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import ExpandMore from '@mui/icons-material/ExpandMore';
import SidebarNavItem from './SidebarNavItem';

/**
 * SidebarNavGroup — Collapsible group for dark sidebar.
 * Compact, clean group headers.
 */
export default function SidebarNavGroup({
  group,
  collapsed = false,
  onNavigate,
  onPin,
  showPinControl = false,
  expandedGroupId,
  onToggleGroup,
  badgeCounts = {},
}) {
  const location = useLocation();

  const hasActiveChild = group.children.some((item) =>
    item.exact
      ? location.pathname === item.path
      : location.pathname === item.path ||
        (item.path !== '/' && location.pathname.startsWith(item.path))
  );

  const open = expandedGroupId !== undefined && onToggleGroup
    ? expandedGroupId === group.id
    : hasActiveChild;

  if (group.topLevel) {
    return (
      <Box>
        {group.children.map((item) => (
          <SidebarNavItem
            key={item.id}
            item={item}
            collapsed={collapsed}
            onNavigate={onNavigate}
            onPin={onPin}
            showPinControl={showPinControl}
            badgeCount={badgeCounts[item.id] || 0}
          />
        ))}
      </Box>
    );
  }

  if (collapsed) {
    return (
      <Box>
        <Box sx={{ my: 0.5, mx: 0.5, borderTop: '1px solid rgba(255,255,255,0.05)' }} />
        {group.children.map((item) => (
          <SidebarNavItem
            key={item.id}
            item={item}
            collapsed={collapsed}
            onNavigate={onNavigate}
            onPin={onPin}
            showPinControl={showPinControl}
            badgeCount={badgeCounts[item.id] || 0}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-label={`${group.label} section`}
        onClick={() => onToggleGroup && onToggleGroup(group.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggleGroup && onToggleGroup(group.id);
          }
        }}
        sx={{
          mt: 1.5,
          mb: 0.5,
          px: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          borderRadius: '6px',
          py: 0.4,
          outline: 'none',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
          '&:focus-visible': {
            outline: '2px solid rgba(59,130,246,0.5)',
            outlineOffset: '1px',
          },
        }}
      >
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.42)',
            fontSize: '0.6875rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.09em',
            userSelect: 'none',
          }}
        >
          {group.label}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            transition: 'transform 150ms ease',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
          }}
        >
          <ExpandMore sx={{ fontSize: 13, color: 'rgba(255,255,255,0.25)' }} />
        </Box>
      </Box>

      <Collapse in={open} timeout={150}>
        {group.children.map((item) => (
          <SidebarNavItem
            key={item.id}
            item={item}
            collapsed={collapsed}
            onNavigate={onNavigate}
            onPin={onPin}
            showPinControl={showPinControl}
            badgeCount={badgeCounts[item.id] || 0}
          />
        ))}
      </Collapse>
    </Box>
  );
}
